import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Seo } from "../components/Seo";
import { PublicLayout, OpenGeneratorButton } from "../components/PublicLayout";
import site from "../content/site.json";

const G = site.guides;

function fmt(d) {
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return d;
  }
}

export default function GuideDetail() {
  const { slug } = useParams();
  const guide = G.items.find((g) => g.slug === slug);

  if (!guide) {
    return (
      <PublicLayout>
        <Seo title="Guide not found | AI Song Prompt Studio" description="This guide could not be found." path={`/guides/${slug || ""}`} />
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center">
          <h1 className="font-heading text-3xl font-bold text-slate-50">Guide not found</h1>
          <p className="mt-3 text-slate-400">The guide you're looking for doesn't exist.</p>
          <Link to="/guides" className="mt-6 inline-block text-[#22D3EE] underline">Back to all guides</Link>
        </section>
      </PublicLayout>
    );
  }

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.description,
    datePublished: guide.date,
    dateModified: guide.date,
    author: { "@type": "Organization", name: "AI Song Prompt Studio" },
    publisher: { "@type": "Organization", name: "AI Song Prompt Studio" },
  };

  return (
    <PublicLayout>
      <Seo title={`${guide.title} | AI Song Prompt Studio`} description={guide.description} path={`/guides/${guide.slug}`} jsonLd={articleLd} />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <Link to="/guides" data-testid="guide-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#22D3EE] transition-colors">
          <ArrowLeft className="w-4 h-4" /> All guides
        </Link>
        <p className="mt-6 text-xs text-slate-500">{fmt(guide.date)}</p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-50 leading-tight">
          {guide.title}
        </h1>
        <p className="mt-4 text-lg text-slate-300 leading-relaxed">{guide.excerpt}</p>

        <div className="mt-10 space-y-8">
          {guide.body.map((sec, i) => (
            <section key={i}>
              <h2 className="font-heading text-xl font-semibold text-slate-50">{sec.heading}</h2>
              {sec.paragraphs.map((p, j) => (
                <p key={j} className="mt-3 text-slate-300 leading-relaxed">{p}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-[#1E2C48] bg-[#0D1424] p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-300 font-medium">Ready to try it in the generator?</p>
          <OpenGeneratorButton testId="guide-open-generator" />
        </div>
      </article>
    </PublicLayout>
  );
}
